import { RefObject, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { titleSmall, textLinkPrimary } from 'styles-js/mixins/typography';
import ProgressBar from 'src/components/progressBar';
import { Disclaimer } from 'src/components/sidebar/disclaimer';
import ModuleList from 'src/modules/programs/components/moduleList';
import Icon from 'src/components/icon';

export type Section = {
  programSectionId: number;
  title: string;
  content: string;
  isCompleted: boolean;
}
export type CurrentModule = {
  currentCode: string;
  name: string;
  sections: Section[];
  sort: number;
}
export type ModulesType = {
  code: string;
  name: string;
  sections: Section[];
  sort: number;
}[];

type Props = {
  bodyRef?: RefObject<HTMLElement>;
};

export default function Modules({ bodyRef }: Props) {
  const { isEnrolled, progress } = useSelector(({ pages }) => pages.programs);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleClick = () => {
    if (bodyRef?.current) {
      window.scrollTo(0, bodyRef.current.offsetTop - MobileDropdownHeight);
    } else {
      window.scrollTo(0, 0);
    }
    setShowDropdown(false);
  };

  return (
    <Wrapper>
      <MobileWrapper>
        <div>
          <MobileDropdown>
            { isEnrolled
              ? <ProgressPercent>{i18n.t('Progress')}{' '}{progress}{'%'}</ProgressPercent>
              : <ProgressPercent>{i18n.t('Contents')}</ProgressPercent>
            }
            <DropdownToggle onClick={() => setShowDropdown(!showDropdown)}>
              {showDropdown ? (
                <>
                  {i18n.t('Hide contents')}
                  <Icon icon="open-up" />
                </>
              ) : (
                <>
                  {i18n.t('Show contents')}
                  <Icon icon="open-down" />
                </>
              )}
            </DropdownToggle>
          </MobileDropdown>
          {isEnrolled && <ProgressBar colour="green" heightSize={5} progress={progress} />}
        </div>
        <MobileModules showContents={showDropdown}>
          <ModuleList handleClick={handleClick} />
        </MobileModules>
      </MobileWrapper>
      <DesktopWrapper>
        <div>
          <DesktopHeader isSmall={!isEnrolled}>
            { isEnrolled
              ? <ProgressPercent>{i18n.t('Progress')}{' '}{progress}{'%'}</ProgressPercent>
              : <ProgressPercent>{i18n.t('Contents')}</ProgressPercent>
            }
          </DesktopHeader>
          {isEnrolled && <ProgressBar colour="green" heightSize={5} progress={progress} />}
        </div>
        <DesktopModules>
          <ModuleList handleClick={handleClick} />
          <Disclaimer />
        </DesktopModules>
      </DesktopWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: sticky;
  top: 82px;
  flex: 1;
  align-self: flex-start;
  overflow-y: auto;

  @media (${props => props.theme.underScreenMedium}) {
    top: 50px;
    width: 100%;
    z-index: 1;
  }
`;

const MobileWrapper = styled.div`
  @media (${props => props.theme.overScreenMedium}) {
    display: none;
  }
`;

const DesktopWrapper = styled.div`
  @media (${props => props.theme.underScreenMedium}) {
    display: none;
  }
`;

const MobileDropdownHeight = 50;

const MobileDropdown = styled.div`
  height: ${MobileDropdownHeight}px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.colorBlueBackground};

  @media (${props => props.theme.underScreenXxsmall}) {
    padding: 0 15px;
  }
`;

const DesktopHeader = styled.div<{ isSmall: boolean }>`
  height: ${props => props.isSmall ? 'auto' : '35px'};
`;

const ProgressPercent = styled.span`
  ${titleSmall};
  flex-grow: 1;
`;

const DropdownToggle = styled.button`
  ${textLinkPrimary};
  border: none;
  background: none;
  display: flex;
  gap: 5px;
`;

const MobileModules = styled.div<{ showContents: boolean }>`
  display: ${props => props.showContents ? 'block' : 'none'};
  overflow-y: auto;
  max-height: calc(100vh - 82px);
`;

const DesktopModules = styled.div`
  margin-top: 10px;
`;
